import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from './cn';
import { Text } from './Typography';

// Single-select selection control. Closed and data-driven — sibling to
// CheckboxGroup with the same option shape ({ value, label,
// description?, disabled? }) and a scalar value (vs the array values
// of a checkbox group). Radix RadioGroup gives us roving keyboard
// focus and arrow-key navigation between items for free.

const ITEM_INDICATOR_CLASSNAME = 'tw-aspect-square tw-h-4 tw-w-4 tw-rounded-full tw-border tw-border-input tw-text-primary tw-transition-colors focus:tw-outline-none focus:tw-ring-0 focus-visible:tw-outline-none focus-visible:tw-ring-0 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 data-[state=checked]:tw-border-primary';

const RadioGroup = React.forwardRef(({
    className: _className,
    style: _style,
    onValueChange,
    options = [],
    value
}, ref) => {
    const groupId = React.useId();

    return (
        <RadioGroupPrimitive.Root
            ref={ref}
            className={cn('tw-grid tw-gap-2')}
            onValueChange={onValueChange}
            value={value}
        >
            {options.map(option => {
                const itemId = `${groupId}-${option.value}`;

                return (
                    <div key={option.value} className="tw-flex tw-items-start tw-gap-2">
                        <span className="tw-flex tw-h-6 tw-items-center">
                            <RadioGroupPrimitive.Item
                                id={itemId}
                                className={ITEM_INDICATOR_CLASSNAME}
                                disabled={option.disabled}
                                value={option.value}
                            >
                                {/* Selected indicator is pure geometry — an
                                    8px filled circle inside the 16px radio.
                                    Bare <span> keeps it crisp without
                                    pulling in an Icon size below 14px. */}
                                <RadioGroupPrimitive.Indicator className="tw-flex tw-items-center tw-justify-center">
                                    <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-current" />
                                </RadioGroupPrimitive.Indicator>
                            </RadioGroupPrimitive.Item>
                        </span>
                        <label
                            htmlFor={itemId}
                            className={cn(
                                'tw-select-none',
                                option.disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
                            )}
                        >
                            <Text tone={option.disabled ? 'muted' : 'foreground'} variant="body">
                                {option.label}
                            </Text>
                            {option.description ? (
                                <Text as="p" tone="muted" variant="body-sm">
                                    {option.description}
                                </Text>
                            ) : null}
                        </label>
                    </div>
                );
            })}
        </RadioGroupPrimitive.Root>
    );
});
RadioGroup.displayName = 'RadioGroup';

export { RadioGroup };
